<script lang="ts">
export interface DataTablePaginationSlotProps {
  pageIndex: number
  page: number
  pageCount: number
  pageSize: number
  rowCount: number
  canPreviousPage: boolean
  canNextPage: boolean
  setPageIndex: (index: number) => void
  setPage: (page: number) => void
  previousPage: () => void
  nextPage: () => void
}
</script>

<script setup lang="ts" generic="TData, TValue">
import type { Column, ColumnDef, ExpandedState, Row, RowSelectionState, SortingState } from '@tanstack/vue-table'
import type { CSSProperties } from 'vue'
import type { TableRowVariants } from '#ui-kit/ui/table'
import {
  FlexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useVueTable,
} from '@tanstack/vue-table'
import { computed, useTemplateRef, watch } from 'vue'
import { cn } from '#ui-kit/lib/utils'
import { Skeleton } from '#ui-kit/ui/skeleton'
import { Table, TableBody, TableCell, tableClass, TableFooter, TableRow } from '#ui-kit/ui/table'
import HeaderRows from './HeaderRows.vue'
import { FLUID_COLUMN_SIZE, UNCAPPED_MAX_SIZE, useColumnSizing } from './useColumnSizing'
import { useFloatingHeader } from './useFloatingHeader'

// it could be either accessorKey or id
type PinnableColumnId = (keyof TData & string) | (string & {})

/** A plain array pins to the left edge; pass `{ left, right }` to pin at either edge. */
type PinnedColumns = PinnableColumnId[] | { left?: PinnableColumnId[]; right?: PinnableColumnId[] }

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    columns: ColumnDef<TData, TValue>[]
    data?: TData[] | null
    rowId?: (row: TData, index: number) => string
    pinnedColumns?: PinnedColumns
    enableRowSelection?: boolean | ((row: Row<TData>) => boolean)
    hideHeader?: boolean
    manualSorting?: boolean
    defaultSorting?: SortingState
    floatingHeader?: boolean
    floatingHeaderOffset?: number
    loading?: boolean
    rowHover?: boolean
    size?: TableRowVariants['size']
    pageSize?: number
    manualPagination?: boolean
    rowCount?: number
  }>(),
  {
    pinnedColumns: () => [],
    enableRowSelection: false,
    hideHeader: false,
    defaultSorting: () => [],
    floatingHeader: false,
    floatingHeaderOffset: 0,
    loading: false,
    rowHover: false,
    size: 'sm',
  },
)

const emit = defineEmits<{
  rowEnter: [row: TData, event: MouseEvent]
  rowMove: [row: TData, event: MouseEvent]
  rowLeave: [row: TData, event: MouseEvent]
}>()

const slots = defineSlots<{
  loading?: (props: {
    columns: Column<TData>[]
    rowCount: number
    cellAttrs: CellAttrs
    size: TableRowVariants['size']
  }) => unknown
  footer?: (props: { columns: Column<TData>[]; cellAttrs: CellAttrs; size: TableRowVariants['size'] }) => unknown
  pagination?: (props: DataTablePaginationSlotProps) => unknown
  expanded?: (props: { row: TData }) => unknown
  empty?: () => unknown
}>()

type CellAttrs = (column: Column<TData>) => { class?: string; style: CSSProperties }

const paginationEnabled = props.pageSize != null

const columnPinning = computed(() =>
  Array.isArray(props.pinnedColumns)
    ? { left: props.pinnedColumns, right: [] }
    : { left: props.pinnedColumns.left ?? [], right: props.pinnedColumns.right ?? [] },
)
const loadingRowCount = computed(() => props.pageSize ?? 10)

const sorting = defineModel<SortingState>('sorting', { default: () => [] })
if (!sorting.value.length) {
  sorting.value = props.defaultSorting
}

const pageIndex = defineModel<number>('pageIndex', { default: 0 })

const expanded = defineModel<ExpandedState>('expanded', { default: () => ({}) })

const rowSelection = defineModel<RowSelectionState>('rowSelection', { default: () => ({}) })

watch(
  () => props.pageSize,
  () => {
    pageIndex.value = 0
  },
)

const table = useVueTable<TData>({
  get data() {
    return props.data ?? []
  },
  get columns() {
    return props.columns
  },
  get getRowId() {
    return props.rowId
  },
  defaultColumn: {
    size: FLUID_COLUMN_SIZE,
    maxSize: UNCAPPED_MAX_SIZE,
    sortUndefined: -1,
  },
  state: {
    get sorting() {
      return sorting.value
    },
    get columnPinning() {
      return columnPinning.value
    },
    get pagination() {
      return paginationEnabled ? { pageIndex: pageIndex.value, pageSize: props.pageSize! } : undefined
    },
    get expanded() {
      return expanded.value
    },
    get rowSelection() {
      return rowSelection.value
    },
  },
  onExpandedChange: (updater) => {
    expanded.value = typeof updater === 'function' ? updater(expanded.value) : updater
  },
  onRowSelectionChange: (updater) => {
    rowSelection.value = typeof updater === 'function' ? updater(rowSelection.value) : updater
  },
  onSortingChange: (updater) => {
    sorting.value = typeof updater === 'function' ? updater(sorting.value) : updater
  },
  onPaginationChange: (updater) => {
    const previous = { pageIndex: pageIndex.value, pageSize: props.pageSize ?? 10 }
    const next = typeof updater === 'function' ? updater(previous) : updater
    pageIndex.value = next.pageIndex
  },
  enableColumnPinning: true,
  get enableRowSelection() {
    return props.enableRowSelection
  },
  autoResetExpanded: false,
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getExpandedRowModel: getExpandedRowModel(),
  getPaginationRowModel: paginationEnabled ? getPaginationRowModel() : undefined,
  get manualSorting() {
    return props.manualSorting
  },
  get manualPagination() {
    return props.manualPagination
  },
  get rowCount() {
    return props.rowCount
  },
})

function setPageIndex(index: number) {
  table.setPageIndex(index)
}

function setPage(page: number) {
  table.setPageIndex(page - 1)
}

function previousPage() {
  table.previousPage()
}

function nextPage() {
  table.nextPage()
}

/**
 * The page a row sits on under the current sort, so a row opened from outside the
 * table can be paged to. Null when the row isn't in `data`.
 */
function getRowPageIndex(rowId: string): number | null {
  const index = table.getSortedRowModel().rows.findIndex((row) => row.id === rowId)

  if (index === -1) {
    return null
  }

  return paginationEnabled ? Math.floor(index / props.pageSize!) : 0
}

defineExpose({ getRowPageIndex })

const tableRef = useTemplateRef<{ getContainer: () => HTMLElement | null }>('tableRef')

const { columnPixelWidths, tableMinWidth } = useColumnSizing(table, () => tableRef.value?.getContainer())

function columnAttrs(column: Column<TData>): { class?: string; style: CSSProperties } {
  const style: CSSProperties = { width: `${columnPixelWidths.value[column.id]}px` }

  if (column.getIsPinned() === 'left') {
    style.left = `${column.getStart('left')}px`
    return { class: 'table-pinned-left', style }
  }

  if (column.getIsPinned() === 'right') {
    style.right = `${column.getAfter('right')}px`
    // Only the innermost right-pinned cell carries the divider: later cells paint
    // over earlier ones, so a strip on every cell would show between pinned cells.
    return { class: cn('table-pinned-right', column.getIsFirstColumn('right') && 'table-pinned-right-edge'), style }
  }

  return { style }
}

function floatingCellAttrs(column: Column<TData>): { class?: string; style: CSSProperties } {
  const base = columnAttrs(column)

  return {
    class: cn(base.class, 'bg-light'),
    style: base.style,
  }
}

const hasColumnFooters = computed(() => table.getVisibleLeafColumns().some((column) => column.columnDef.footer != null))

const floatingEl = useTemplateRef<HTMLDivElement>('floatingEl')

useFloatingHeader({
  enabled: () => props.floatingHeader && !props.hideHeader,
  bodyContainer: () => tableRef.value?.getContainer(),
  overlay: () => floatingEl.value,
  offset: () => props.floatingHeaderOffset,
})
</script>

<template>
  <Table ref="tableRef" class="table-fixed min-w-(--dt-min-width)" :style="{ '--dt-min-width': tableMinWidth }">
    <HeaderRows v-if="!hideHeader" :header-groups="table.getHeaderGroups()" :cell-attrs="columnAttrs" />
    <TableBody>
      <template v-if="loading">
        <slot
          name="loading"
          :columns="table.getVisibleLeafColumns()"
          :row-count="loadingRowCount"
          :cell-attrs="columnAttrs"
          :size="size"
        >
          <TableRow v-for="i in loadingRowCount" :key="`loading-${i}`" :size="size">
            <TableCell v-for="column in table.getVisibleLeafColumns()" :key="column.id" v-bind="columnAttrs(column)">
              <div class="px-2">
                <Skeleton class="h-4" />
              </div>
            </TableCell>
          </TableRow>
        </slot>
      </template>
      <template v-else-if="table.getRowModel().rows?.length">
        <template v-for="row in table.getRowModel().rows" :key="row.id">
          <TableRow
            :size="size"
            class="data-expanded:border-b-0"
            :class="[
              rowHover && 'hover:bg-page [&:hover_.table-pinned-left]:bg-page [&:hover_.table-pinned-right]:bg-page',
            ]"
            :data-row-id="row.id"
            :data-state="row.getIsSelected() ? 'selected' : undefined"
            :data-expanded="row.getIsExpanded() ? 'true' : undefined"
            @mouseenter="emit('rowEnter', row.original, $event)"
            @mousemove="emit('rowMove', row.original, $event)"
            @mouseleave="emit('rowLeave', row.original, $event)"
          >
            <TableCell v-for="cell in row.getVisibleCells()" :key="cell.id" v-bind="columnAttrs(cell.column)">
              <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
            </TableCell>
          </TableRow>
          <TableRow v-if="slots.expanded && row.getIsExpanded()" data-slot="table-expanded-row" class="h-auto">
            <TableCell :colspan="table.getVisibleLeafColumns().length">
              <slot name="expanded" :row="row.original" />
            </TableCell>
          </TableRow>
        </template>
      </template>
      <template v-else>
        <TableRow data-slot="table-empty-row" class="h-auto">
          <TableCell :colspan="table.getVisibleLeafColumns().length">
            <slot name="empty">
              <span class="flex h-24 items-center justify-center">No results.</span>
            </slot>
          </TableCell>
        </TableRow>
      </template>
    </TableBody>
    <TableFooter v-if="slots.footer || hasColumnFooters">
      <slot name="footer" :columns="table.getVisibleLeafColumns()" :cell-attrs="columnAttrs" :size="size">
        <TableRow v-for="footerGroup in table.getFooterGroups()" :key="footerGroup.id" :size="size">
          <TableCell v-for="footer in footerGroup.headers" :key="footer.id" v-bind="columnAttrs(footer.column)">
            <FlexRender
              v-if="!footer.isPlaceholder"
              :render="footer.column.columnDef.footer"
              :props="footer.getContext()"
            />
          </TableCell>
        </TableRow>
      </slot>
    </TableFooter>
  </Table>

  <slot
    v-if="paginationEnabled"
    name="pagination"
    :page-index="pageIndex"
    :page="pageIndex + 1"
    :page-count="table.getPageCount()"
    :page-size="pageSize!"
    :row-count="table.getRowCount()"
    :can-previous-page="table.getCanPreviousPage()"
    :can-next-page="table.getCanNextPage()"
    :set-page-index="setPageIndex"
    :set-page="setPage"
    :previous-page="previousPage"
    :next-page="nextPage"
  />

  <Teleport to="body">
    <div
      v-if="floatingHeader && !hideHeader"
      ref="floatingEl"
      data-slot="table-container"
      class="invisible fixed z-30 overflow-hidden"
      :style="{ top: `${floatingHeaderOffset}px` }"
    >
      <table
        data-slot="table"
        :class="cn(tableClass, 'table-fixed', 'min-w-(--dt-min-width)')"
        :style="{ '--dt-min-width': tableMinWidth }"
      >
        <HeaderRows :header-groups="table.getHeaderGroups()" :cell-attrs="floatingCellAttrs" />
      </table>
    </div>
  </Teleport>
</template>
